<template>
  <div class="rubric-with-grading-details-form">
    <eg-card
      :label="$t('common.rubric')"
      onboarding-key="onboarding:clicked:submissionFormRubricSelectQuestionIcon"
      data-testid="submission-form-rubric-select"
    >
      <template #extra-actions>
        <eg-button
          v-b-toggle.rubric-library-sidebar
          type="secondary"
          class="ml-3"
        >
          {{ $t("rubrics.selectRubricToStart") }}
        </eg-button>
      </template>
      <eg-rubric-card
        class="mt-4"
        :selected-rubric="localValue.selectedRubric"
        @delete-rubric="removeRubric"
      />
      <slot name="upload-essay" />
      <rubric-library
        :value="localValue.selectedRubric"
        show-add-new
        @input="updateRubric"
      />
    </eg-card>

    <reference-material-writing-prompt
      :selected-source-material="localValue.selectedSourceMaterial"
      :is-loading="showSourceMaterialLoading"
      :additional-source-materials="additionalSourceMaterials"
      @delete="deleteSourceMaterial"
      @select-source-material="updateSourceMaterial"
    />

    <grading-details-form
      :value="localValue.gradingDetails"
      :rubric="localValue.selectedRubric"
      :is-edit="isEdit"
      @input="updateGradingDetails"
    />
  </div>
</template>

<script>
import EgRubricCard from "@/components/rubrics/components/eg_rubric_card.vue";
import GradingDetailsForm from "@/components/rubrics/gradingDetails/grading_details_form.vue";
import { LSK_SUGGESTION_FORM_SELECTED_RUBRIC } from "@/constants";
import RubricLibrary from "@/components/rubrics/rubric_library.vue";
import isLMSMixin from "@/mixins/isLMS";
import ReferenceMaterialWritingPrompt from "@/components/source-materials/reference_material_writing_prompt";
import { getSourceMaterial } from "@/api";

export default {
  name: "GradingSettingsForm",
  components: {
    ReferenceMaterialWritingPrompt,
    GradingDetailsForm,
    EgRubricCard,
    RubricLibrary,
  },

  mixins: [
    // this.isLMS
    isLMSMixin,
  ],

  props: {
    value: {
      type: Object,
      default: () => ({
        selectedRubric: null,
        selectedSourceMaterial: null,
        gradingDetails: {},
      }),
    },

    isEdit: {
      type: Boolean,
      default: false,
    },
    isLoadingSourceMaterial: {
      type: Boolean,
      default: false,
    },

    additionalSourceMaterials: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isLoadingInternal: false,
      localValue: {
        selectedRubric: this.value.selectedRubric,
        selectedSourceMaterial: this.value.selectedSourceMaterial,
        gradingDetails: this.value.gradingDetails,
      },
    };
  },

  computed: {
    showSourceMaterialLoading() {
      return this.isLoadingSourceMaterial || this.isLoadingInternal;
    },
  },

  watch: {
    value: {
      handler(newVal) {
        this.localValue = newVal;
      },
      deep: true,
    },
  },

  async mounted() {
    if (!this.value.selectedRubric) {
      const suggestedRubric = JSON.parse(
        localStorage.getItem(LSK_SUGGESTION_FORM_SELECTED_RUBRIC)
      );

      if (suggestedRubric) {
        this.updateRubric(suggestedRubric);
      }
    }

    await this.loadSourceMaterial();
  },

  methods: {
    async loadSourceMaterial() {
      const sourceMaterialId = this.value.gradingDetails?.sourceMaterialId;
      if (!sourceMaterialId || this.localValue.selectedSourceMaterial) {
        return;
      }

      this.isLoadingInternal = true;
      try {
        const { data } = await getSourceMaterial(sourceMaterialId);
        this.localValue.selectedSourceMaterial = data;
      } catch (error) {
        console.error("Failed to load source material:", error);
      } finally {
        this.isLoadingInternal = false;
      }
    },

    setSourceMaterialData(sourceMaterial) {
      const updatedGradingDetails = {
        ...this.localValue.gradingDetails,
        sourceMaterialId: sourceMaterial?.id || null,
      };
      this.$emit("input", {
        ...this.localValue,
        selectedSourceMaterial: sourceMaterial,
        gradingDetails: updatedGradingDetails,
      });
      this.localValue.selectedSourceMaterial = sourceMaterial;
      this.localValue.gradingDetails = updatedGradingDetails;
    },
    updateSourceMaterial(newSourceMaterial) {
      this.setSourceMaterialData(newSourceMaterial);
    },
    deleteSourceMaterial() {
      this.setSourceMaterialData(null);
    },
    updateRubric(newRubric) {
      this.$emit("input", {
        ...this.localValue,
        selectedRubric: newRubric,
      });
      this.localValue.selectedRubric = newRubric;
    },

    updateGradingDetails(newDetails) {
      this.$emit("input", {
        ...this.localValue,
        gradingDetails: newDetails,
      });
      this.localValue.gradingDetails = newDetails;
    },

    removeRubric() {
      this.$emit("input", {
        ...this.localValue,
        selectedRubric: null,
      });
      this.localValue.selectedRubric = null;
      localStorage.removeItem(LSK_SUGGESTION_FORM_SELECTED_RUBRIC);
    },
  },
};
</script>

<style scoped>
.rubric-with-grading-details-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}
</style>
